/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: services.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import Statics from '@/constants/statics';
import BasicComponents from '@/shared/basic.components';
import Section from '@/shared/section';
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Services: FunctionComponent = () => {
  const { t } = useTranslation('home-page');

  return (
    <Section id="services" className="bg-black/80">
      <div className="flex flex-col items-start justify-start gap-5">
        <BasicComponents.SectionHeader
          title={t('services.title')}
          subtitle={t('services.subtitle')}
        />
        <div className="w-full grid grid-cols-1 gap-5">
          {Statics.ServiceItems.map(({ id, src, title, desc }, idx: number) => (
            <motion.div
              key={id}
              className="col-span-1 w-full flex flex-col lg:flex-row items-center justify-start gap-10 rounded-xl overflow-hidden py-5"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: 50 + idx * 25,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              <img
                src={src}
                alt="service"
                className={`${idx % 2 === 0 ? 'lg:order-last order-first':'order-first'} w-full h-[275px] object-cover rounded-lg transition-all duration-500`}
              />
              <div className="w-full h-fit flex flex-col items-center justify-start gap-2">
                <h4 className="w-full text-lg lg:text-xl text-left text-theme-900 font-montserrat font-semibold uppercase">
                  {t(title)}
                </h4>
                <div className="w-full h-[2px] bg-theme-900 mb-3"></div>
                <p className="text-md lg:text-lg text-white text-justify font-roboto font-light">{t(desc)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default Services;
